import { z } from 'zod'

/**
 * The time zones page's view state, in the URL.
 *
 * `from`, `store` and `ads` say which page the reader came from — the header
 * writes them — so the Add row starts on that page and store, and the page
 * offers the way back. A link carries them, so a person or an agent opening
 * `/time-zones?from=/marketing/ams&store=7` lands ready to add AMS View on
 * store 7. What is picked before Add is an unsaved draft, so it is not here.
 *
 * Every field optional and caught, as everywhere: a query string is input.
 */
export const timeZonesSearchSchema = z.object({
  /** The path the reader came from; only an in-app path, never a full URL. */
  from: z
    .string()
    .max(256)
    .regex(/^\/(?!\/)/)
    .optional()
    .catch(undefined)
    .describe(
      'The in-app path the reader came from; the Add row starts on that page, and a "Back to" button returns there.',
    ),
  /** The store that page showed. */
  store: z
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined)
    .describe('The store id that page showed; the Add row starts on it.'),
  /** Whether that page reads an ads profile's hours. */
  ads: z
    .boolean()
    .optional()
    .catch(undefined)
    .describe(
      "Whether that page reads an ads profile's hours, so the store's ads zone is its fallback.",
    ),
})

export type TimeZonesSearch = z.infer<typeof timeZonesSearchSchema>

/** Drop what carries nothing, so a URL holds only what differs. */
export function withoutTimeZonesSearchDefaults(search: TimeZonesSearch): TimeZonesSearch {
  const pruned: TimeZonesSearch = {}
  if (search.from !== undefined) pruned.from = search.from
  if (search.store !== undefined) pruned.store = search.store
  if (search.ads) pruned.ads = true
  return pruned
}
